  import { Viewer, Worker } from "@react-pdf-viewer/core";
  import "@react-pdf-viewer/core/lib/styles/index.css";
  import html2pdf from "html2pdf.js";
  import { useEffect, useMemo, useRef, useState } from "react";
  import DataTable, { TableColumn } from "react-data-table-component";
  import { Navigate, useLocation, useNavigate } from "react-router-dom";
  import { SpinnerCircular } from "spinners-react";
  import { useGetFieldLabel } from "../../contexts/LanguageContext";
  import { useSetStuffToRenderOnNavbar } from "../../contexts/NavbarCommonSpace";
  import { isUserOrArchitect } from "../../hoc/isUserOrArchitect";
  import {
    TApplicationField,
    TApplicationFieldOption,
  } from "../../hooks/application-fields.hooks";
  import {
    useAllFields,
    useApplication,
    useApplicationFormSteps,
    useGeneratedDocs,
    usePaymentReceipts,
  } from "../../hooks/applications.hooks";
  import { useToggle } from "../../hooks/common";
  import { TLevel2PaymentReceipt } from "../../hooks/level-2-payment-approval.hooks";
  import { useAllApplicationReviews } from "../../hooks/resubmission.hooks";
  import { RenderAllReviews } from "../../pages/LevelTwo/RenderAllReviews";
  import { capitalize, downloadResource } from "../../utils/common";
  import { formatDate } from "../../utils/date";
  import { DocumentPreviewDialog } from "../CommonComponents/DialogConfirmation/DocPreviewDialog";
  import {
    DownloadButton,
    ViewButton,
  } from "../CommonComponents/EditComponent/EditComponent";
  import { tableCustomStyles } from "../CommonComponents/Table/Table";
  import style from "./ViewApplication.module.css";
  import { useApplicationOverride } from "../../hooks/level-3-application-approval.hooks";
  import {OverrideReason} from "../CommonComponents/OverrideReason/OverrideReason";
  
  type PropTypes = {
    data: Record<string, unknown>;
    index: number;
    field: TApplicationField & {
      options?: TApplicationFieldOption[];
    };
  };

  type InfoRowProps = {
    label: string;
    value: string;
  };

  
  export function ShowField({ field, data, index }: PropTypes) {
    let val = "-";
    const convert = useGetFieldLabel();
  
    switch (field.type) {
      case "boolean": {
        if (data[field.uid]) val = "Yes";
        else if (data[field.uid] === false) val = "No";
        break;
      }
      case "configurable-select":
      case "select": {
        val =
          (field.options || []).find((e) => e.id === data[field.uid])?.name ||
          "-";
        break;
      }
      case "date": {
        val = data[field?.uid] ? formatDate(String(data[field?.uid])) : "-";
        break;
      }
      case "area": {
        val = data[field?.uid]
          ? `${data[field?.uid]} ${data[`unit_${field?.uid}`] ?? "sq. mt."}`
          : "-";
        break;
      }
      case "integer":
      case "float":
      default: {
        val = String(data[field?.uid] ?? "-");
      }
    }
    return (
      <div className="flex items-center space-x-2 md:space-x-6 mb-2">
        <p className="text-grey-600 text-sm md:text-base font-semibold shrink-0 basis-1/3">{`${index}. ${convert(
          field
        )}`}</p>
        <p className="text-grey-600 text-sm md:text-base shrink-0">:</p>
        <p className="text-grey-600 text-sm md:text-base grow inline-block min-w-[100px] text-wrap break-words">
          {val}
        </p>
      </div>
    );
  }
  
  export const ViewApplication = isUserOrArchitect(() => {
    const [document, setDocument] = useState<{ url: string } | null>(null);
    const location = useLocation();
    const [reviewDialog, toggleReviewDialog] = useToggle();
    const id = location.state?.id;
    const allReviewsQuery = useAllApplicationReviews({ id: +id });
    const receiptsQuery = usePaymentReceipts({ enabled: true, id: +id });
  
    const navigate = useNavigate();
  
  
  
    const ALLOWED_DEMAND_NOTE_STATUSES = [
      "Pending Payment",
      "Returned For Repayment By Level 2 Reviewer",
      "Returned For Repayment By Level 3 Reviewer",
      "Pending Payment Level 2 Review",
      "Pending Final Review",
      "Payment Rejected By Level 2 Reviewer",
      "Payment Rejected By Level 3 Reviewer",
      "Pending Payment & Application Rejection Confirmation",
      "Completed",
    ];
  
    const BLOCKED_STATUSES = [
      "Pending Payment Level 2 Review",
      "Pending Final Review",
    ];
  
    const contentRef = useRef(null);
    // Ref to the content to be captured for the PDF
    const handleDownloadPDF = () => {
      // Get the element to generate the PDF from
      const content = contentRef.current;
      // Set PDF options
      const options = {
        margin: 1,
        filename: "page.pdf",
        image: {
          type: "jpeg",
          quality: 0.98,
        },
        html2canvas: {
          scale: 2,
        },
        jsPDF: {
          unit: "in",
          format: "letter",
          orientation: "portrait",
        },
      };
      // const updatedData = `<div><p>Application Id</p>${content}</div>`
      // Generate and download the PDF
      html2pdf().from(content).set(options).save();
    };
  
    const {
      isError: isError1,
      isFetching: isFetching1,
      data: application,
    } = useApplication({ id });
  
    const appOverrideQuery = useApplicationOverride(application?.applicationId, "application");
    const paymentOverrideQuery = useApplicationOverride(application?.applicationId, "payment");
  
    const {
      data: allFields,
      isError: isError3,
      isFetching: isFetching3,
    } = useAllFields({
      serviceName: String(application?.name_of_service || ""),
    });
  
    const { data: generatedDocs } = useGeneratedDocs({ id: +id, enabled: true });
  
    const {
      data: result,
      isError: isError2,
      isFetching: isFetching2,
    } = useApplicationFormSteps(Number(application?.name_of_service));
  
    const steps = result?.data || [];
    const isFetching =
      isFetching1 || isFetching2 || isFetching3 || receiptsQuery.isLoading;
  
    const isError =
      isError1 ||
      isError2 ||
      isError3 ||
      receiptsQuery.isError ||
      (!isFetching && (!application || !steps?.length));
  
    // Function to check if a document requires digital signature
    const isDigitalSignatureRequired = (documentConfigId: number): boolean => {
      const docForm = steps?.find((e) => e.type === "Documents");
      if (!docForm) return false;
  
      const documentConfig = docForm?.documentConfigs?.find(
        (e) => e.document_config_id === documentConfigId
      );
  
      return (
        documentConfig?.Req_digital_Sign === 1 ||
        documentConfig?.Req_digital_Sign === true
      );
    };


    // Function to determine if documents should be filtered based on status
    const shouldHideDigitalSignatureDocuments = (): boolean => {
      return BLOCKED_STATUSES.includes(application?.status || "");
    };
  
    const setStuff = useSetStuffToRenderOnNavbar();
  
    useEffect(() => {
      setStuff(
        <div className="md:ml-auto md:mr-8">
          <p className="text-grey-600">
            <span className="w-28 font-semibold inline-block">
              Application ID
            </span>
            <span className="text-grey-600 font-semibold mx-2">:</span>
            <span>{application?.applicationId || "Nil"}</span>
          </p>
          <p className="text-grey-600 flex items-center">
            <span className="w-28 text-grey-600 font-semibold inline-block">
              Status
            </span>
            <span className="text-grey-600 font-semibold mx-2">:</span>
            <span className="text-primary">{application?.status || "Nil"}</span>
          </p>
        </div>
      );
      return () => setStuff(null);
    }, [setStuff, application]);
  
    const openInNewTab = (url: string) => {
      window.open(url, "_blank", "noopener,noreferrer");
    };
  
    const simpleDownload = (url: string) => {
      window.open(url, "_blank");
    };
  
    const applicationStatus = application?.status;
  
    const receiptsTable = useMemo(
      (): TableColumn<TLevel2PaymentReceipt>[] => [
        {
          name: "Sr. No.",
          selector: (_, i) => (i || 0) + 1,
          center: true,
        },
        {
          name: "Receipt No",
          selector: (d) => String(d?.receipt_no || "-"),
          center: true,
        },
        {
          name: "Paid Amount",
          selector: (d) => (d?.amount_paid ? `${d.amount_paid} INR` : "-"),
          center: true,
        },
        {
          name: "Payment Date",
          center: true,
          cell: (d) => (d?.payment_date ? formatDate(d.payment_date) : "-"),
        },
        {
          name: "Receipt",
          center: true,
          cell: (d, i) => (
            <div className="flex space-x-4">
              <ViewButton
                // onClick={() =>
                //   setDocument({
                //     url: String(d?.receipt_file),
                //   })
                // }
                onClick={() => {
                  openInNewTab(String(d?.receipt_file));
                }}
              />
              <DownloadButton
                // onClick={() => downloadResource(String(d?.receipt_file))}
                onClick={() => simpleDownload(String(d?.receipt_file))}
              />
            </div>
          ),
        },
      ],
      []
    );
  
    const nocColumns = useMemo((): TableColumn<Record<string, unknown>>[] => {
      const noc = steps?.find((e) => e.type === "NOC");
      if (!noc) return [];
      return [
        {
          name: "Sr. No.",
          selector: (_, i) => (i || 0) + 1,
          center: true,
          grow: 0.5,
        },
        {
          name: "NOC Type",
          selector: (d) =>
            String(
              (noc.fields?.find((e) => e.uid === "noc_type")?.options || []).find(
                (e) => e.id === d.noc_type
              )?.name || "-"
            ),
          center: true,
          grow: 3,
        },
        {
          name: "NOC No.",
          selector: (d) => String(d?.noc_number || "-"),
          center: true,
          grow: 2,
          width: "120px",
        },
        {
          name: "NOC Date",
          selector: (d) => formatDate(String(d?.noc_date || "x")),
          center: true,
          width: "130px",
        },
        {
          name: "View",
          center: true,
          width: "180px",
          cell: (d, i) => (
            <div className="space-x-4">
              {/* <button
                className="text-lg"
                onClick={() => openInNewTab(String(d?.noc_doc))}
              >
                <i className="m-0 fa fa-eye"></i>
              </button> */}
              {/* {application?.status === "Completed" && (
                <button
                  className="text-lg"
                  onClick={() => downloadResource(String(d?.noc_doc))}
                >
                  <i className="m-0 fa fa-download"></i>
                </button>
              )} */}
              <ViewButton
                // onClick={() => setDocument({ url: String(d?.noc_doc) })}
                onClick={() => {
                  //openInNewTab(String(d?.url));
                  openInNewTab(String(d?.noc_doc));
                }}
              />
              {application?.status === "Completed" && (
                <DownloadButton
                  // onClick={() => downloadResource(String(d?.noc_doc))}
                  //onClick={() => simpleDownload(String(d?.url))}
                  onClick={() => simpleDownload(String(d?.noc_doc))}
                />
              )}
            </div>
          ),
        },
      ];
    }, [steps, applicationStatus]);
  
    const selectedFields = useMemo(
      () =>
        (steps || [])
          .filter((e) => !["Documents", "NOC"].includes(e.type))
          .flatMap((e) => e.fields || [])
          .reduce<Map<string, true>>((acc, e) => acc.set(e.uid, true), new Map()),
      [steps]
    );
  
    const generatedDocsColumns = useMemo((): TableColumn<{
      type: string;
      id: number;
      url: string;
      date?: string;
    }>[] => {
      return [
        {
          name: "Sr. No.",
          selector: (_, i) => (i || 0) + 1,
          center: true,
          grow: 0.5,
        },
        {
          name: "Type",
          cell: (d) => capitalize(d.type),
          center: true,
          grow: 3,
        },
        {
          name: "Date",
          selector: (d) => formatDate(String(d?.date || "x")),
          center: true,
          width: "125px",
        },
        {
          name: "View",
          center: true,
          width: "145px",
          cell: (d, i) => (
            <div className="space-x-4">
              {/* <button
                className="text-lg"
                onClick={() => openInNewTab(String(d?.url))}
              >
                <i className="m-0 fa fa-eye"></i>
              </button>
              {applicationStatus === "Completed" && (
                <button
                  className="text-lg"
                  onClick={() => downloadResource(String(d?.url))}
                >
                  <i className="m-0 fa fa-download"></i>
                </button>
              )} */}
              <ViewButton
                // onClick={() => setDocument({ url: String(d?.url) })}
                onClick={() => {
                  openInNewTab(String(d?.url));
                }}
              />
              {application?.status === "Completed" && (
                <DownloadButton
                  // onClick={() => downloadResource(String(d?.url))}
                  onClick={() => simpleDownload(String(d?.url))}
                />
              )}
            </div>
          ),
        },
      ];
    }, [applicationStatus]);
  
    // const documentTable = useMemo((): {
    //   // docs: { name: string; url: string }[];
    //   // documentColumns: TableColumn<{
    //   //   name: string;
    //   //   url: string;
    //   // }>[];
    //   docs: { name: string; url: string; configId: number }[];
    //   documentColumns: TableColumn<{
    //     name: string;
    //     url: string;
    //     configId: number;
    //   }>[];
    // } => {
    //   const docForm = steps?.find((e) => e.type === "Documents");
  
    //   if (!docForm) return { docs: [], documentColumns: [] };
    //   return {
    //     docs: Object.entries(application?.documents || {})
    //       .map(([k, url]) => {
    //         const hit = docForm?.documentConfigs?.find(
    //           (e) => e.document_config_id === +k
    //         )?.document_label;
  
    //         if (!hit) return null;
    //         // return { name: hit, url };
    //         return { name: hit, url, configId: +k };
    //       })
    //       .filter((x): x is NonNullable<typeof x> => !!x),
  
    //     documentColumns: [
    //       {
    //         name: "Sr. No.",
    //         selector: (_, i) => (i || 0) + 1,
    //         center: true,
    //         grow: 0.5,
    //       },
    //       {
    //         name: "Document Name",
    //         // selector: (d) => d.name,
    //         cell: (d) => <span className="text-center">{d.name}</span>,
    //         center: true,
    //         grow: 3,
    //       },
    //       {
    //         name: "View",
    //         center: true,
    //         width: "145px",
    //         cell: (d, i) => (
    //           <div className="space-x-4">
    //             {/* <button
    //               className="text-lg"
    //               onClick={() => openInNewTab(String(d?.url))}
    //             >
    //               <i className="m-0 fa fa-eye"></i>
    //             </button>
    //             {application?.status === "Completed" && (
    //               <button
    //                 className="text-lg"
    //                 onClick={() => downloadResource(String(d?.url))}
    //               >
    //                 <i className="m-0 fa fa-download"></i>
    //               </button>
    //             )} */}
    //             <ViewButton
    //               // onClick={() => setDocument({ url: String(d?.url) })}
    //               onClick={() => window.open(String(d?.url), "_blank")}
    //             />
    //             {application?.status === "Completed" && (
    //               <DownloadButton
    //                 // onClick={() => downloadResource(String(d?.url))}
    //                 onClick={() => simpleDownload(String(d?.url))}
    //               />
    //             )}
    //           </div>
    //         ),
    //       },
    //     ],
    //   };
    // }, [steps, application]);
  
    const documentTable = useMemo((): {
      docs: { name: string; url: string; configId: number }[];
      documentColumns: TableColumn<{
        name: string;
        url: string;
        configId: number;
      }>[];
    } => {
      const docForm = steps?.find((e) => e.type === "Documents");
  
      if (!docForm) return { docs: [], documentColumns: [] };
  
      // First, create the docs array
      let docs = Object.entries(application?.documents || {})
        .map(([k, url]) => {
          const hit = docForm?.documentConfigs?.find(
            (e) => e.document_config_id === +k
          )?.document_label;
  
          if (!hit) return null;
          return { name: hit, url, configId: +k };
        })
        .filter((x): x is NonNullable<typeof x> => !!x);
  
      // Then apply the filtering if needed
      if (shouldHideDigitalSignatureDocuments()) {
        docs = docs.filter((doc) => !isDigitalSignatureRequired(doc.configId));
      }
  
      return {
        docs,
        documentColumns: [
          {
            name: "Sr. No.",
            selector: (_, i) => (i || 0) + 1,
            center: true,
            grow: 0.5,
          },
          {
            name: "Document Name",
            cell: (d) => <span className="text-center">{d.name}</span>,
            center: true,
            grow: 3,
          },
          {
            name: "View",
            center: true,
            width: "145px",
            cell: (d, i) => (
              <div className="space-x-4">
                <ViewButton
                  onClick={() => window.open(String(d?.url), "_blank")}
                />
                {application?.status === "Completed" && (
                  <DownloadButton
                    onClick={() => simpleDownload(String(d?.url))}
                  />
                )}
              </div>
            ),
          },
        ],
      };
    }, [steps, application]);
  
    if (isNaN(id)) {
      return <Navigate to={"/user-applications"} replace />;
    }
  
    if (isFetching || isError) {
      return (
        <div className="flex justify-center items-center p-8">
          {isError ? (
            <p>Something went wrong while fetching info!</p>
          ) : (
            <SpinnerCircular />
          )}
        </div>
      );
    }
  
    const PrintData = () => {
      return (
        <div style={{ display: "none" }}>
          <div ref={contentRef}>
            <h4 className="text-center text-grey-600 font-semibold mb-8 text-2xl">
              Application
            </h4>
            <div className="flex flex-col ml-auto mr-8 justify-items-end items-end">
              <p className="text-grey-600">
                <span className="w-28 font-semibold inline-block">
                  Application ID
                </span>
                <span className="text-grey-600 font-semibold mx-2">:</span>
                <span>{application?.applicationId || "Nil"}</span>
              </p>
              <p className="text-grey-600 flex items-center">
                <span className="text-grey-600 font-semibold inline-block">
                  Status
                </span>
                <span className="text-grey-600 font-semibold mx-2">:</span>
                <span className="text-primary">
                  {application?.status || "Nil"}
                </span>
              </p>
            </div>
            <OverrideReason
                status={application?.status}
                applicationReason={appOverrideQuery.data?.data?.reason}
                paymentReason={paymentOverrideQuery.data?.data?.reason}
            />
            <ApplicationList />
          </div>
        </div>
      );
    };
  
    const ApplicationList = () => {
      let i = 0;
      return (
        <div>
          {(allFields || [])
            .filter((e) => selectedFields.has(e.uid) || !!application?.[e.uid])
            .map((e) => (
              <ShowField
                field={e}
                index={++i}
                key={`field-${e.id}`}
                data={application || {}}
              />
            ))}
        </div>
      );
    };
  
    return (
      <div className="px-4 pb-4 md:p-4">
        <div className="bg-white p-3 md:p-8">
          <h4 className="text-center text-grey-600 font-semibold mb-4 text-2xl">
            Application
          </h4>
          <div className="flex justify-end mb-4">
            <button
              type="button"
              className="text-base font-bold disabled:text-gray-500"
              disabled={!allReviewsQuery.data?.length}
              onClick={toggleReviewDialog}
            >
              <i className="fa fa-history"></i>
              View All Reviews
            </button>
          </div>
          <OverrideReason
              status={application?.status}
              applicationReason={appOverrideQuery.data?.data?.reason}
              paymentReason={paymentOverrideQuery.data?.data?.reason}
          />
          <ApplicationList />
          <PrintData />
          {!!nocColumns?.length &&
            !!(application?.nocs as Record<string, unknown>[])?.length && (
              <>
                <h4 className="text-base my-4 font-bold text-grey-600">NOC : </h4>
                <DataTable
                  keyField="url"
                  className="mt-4"
                  columns={nocColumns}
                  customStyles={tableCustomStyles}
                  data={(application?.nocs as Record<string, unknown>[]) || []}
                />
              </>
            )}
          {!!documentTable.docs?.length && (
            <>
              <h4 className="text-base my-4 font-bold text-grey-600">
                Documents :{" "}
              </h4>
              <DataTable
                keyField="url"
                className="mt-4"
                data={documentTable.docs}
                customStyles={tableCustomStyles}
                columns={documentTable.documentColumns}
              />
            </>
          )}
          {!!receiptsQuery?.data?.length && (
            <>
              <h4 className="text-base my-4 font-bold text-grey-600">
                Payment Receipts :{" "}
              </h4>
              <DataTable
                keyField="url"
                className="mt-4"
                columns={receiptsTable}
                data={receiptsQuery?.data}
                customStyles={tableCustomStyles}
              />
            </>
          )}
          {/* {!!generatedDocs?.length &&
            ALLOWED_DEMAND_NOTE_STATUSES.includes(application?.status || "") && (
              <>
                <h4 className="text-base my-4 font-bold text-grey-600">
                  Application Documents :{" "}
                </h4>
                <DataTable
                  keyField="url"
                  className="mt-4"
                  data={generatedDocs}
                  columns={generatedDocsColumns}
                  customStyles={tableCustomStyles}
                />
              </>
            )} */}
          {!!generatedDocs?.length &&
            (ALLOWED_DEMAND_NOTE_STATUSES.includes(application?.status || "") ||
              application?.status === "Completed") && (
              <>
                <h4 className="text-base my-4 font-bold text-grey-600">
                  Application Documents :{" "}
                </h4>
                <DataTable
                  keyField="url"
                  className="mt-4"
                  data={generatedDocs.filter((d) => {
                    if (application?.status === "Completed") {
                      // allow everything including certificate
                      return d;
                    }
                    // otherwise, exclude certificate
                    return d.type !== "certificate";
                  })}
                  columns={generatedDocsColumns}
                  customStyles={tableCustomStyles}
                />
              </>
            )}
  
          <div className={`${style.buttonSection} gap-5`}>
            <button
              // className={style.closeButton}
              className="bg-black px-6 py-2 rounded-full font-bold text-white"
              onClick={() => navigate("/user-applications")}
            >
              Back
            </button>
            <button
              type="button"
              onClick={handleDownloadPDF}
              className="bg-primary px-6 py-2 rounded-full font-bold text-white"
            >
              Download
            </button>
          </div>
        </div>
        {/* </div> */}
        {reviewDialog && (
          <RenderAllReviews
            title="Reviews"
            onClose={toggleReviewDialog}
            reviews={allReviewsQuery.data ?? []}
          />
        )}
  
        {document && (
          <DocumentPreviewDialog
            onClose={() => setDocument(null)}
            open={!!document}
          >
            {document.url.endsWith("pdf") ? (
              <Worker
                workerUrl={`https://unpkg.com/pdfjs-dist@2.16.105/build/pdf.worker.min.js`}
              >
                <Viewer fileUrl={document.url} />
              </Worker>
            ) : (
              <img
                src={document.url}
                className="object-contain w-full h-full"
                alt="Document"
              />
            )}
          </DocumentPreviewDialog>
        )}
      </div>
    );
  });
